import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import LiveStatsChart from './LiveStatsChart';
import { GrabberIcon } from './icons/DashboardIcons';
import {
  emptyGrabberStats,
  formatGrabberCount,
  formatGrabberRate,
  formatSessionDuration,
  GrabberStatsSnapshot,
} from '../utils/grabberStats';
import type { TourGrabberSimData } from '../types/tourSim';
import './CrackerController.css';
import './ConfigManager.css';
import './MailerManager.css';
import './GrabberManager.css';
import './AtlasPageLayout.css';

interface GrabberStats extends GrabberStatsSnapshot {}

interface GrabberManagerProps {
  licenseData: any;
  vpsConfig: any;
  isScanning?: boolean;
  /** Affiche l'interface complète sans accès (aperçu verrouillé) */
  previewMode?: boolean;
  /** Simulation data from TourGuide — overrides real state when provided */
  tourSim?: TourGrabberSimData;
  /** Called when user clicks "Stop" during simulation */
  simOnStop?: () => void;
  /** Tutorial mode — bypass license gate so user can interact with real UI */
  tutorialMode?: boolean;
}

const GrabberManager: React.FC<GrabberManagerProps> = ({ licenseData, vpsConfig, isScanning = false, previewMode = false, tourSim, simOnStop, tutorialMode }) => {
  const [isGrabbing, setIsGrabbing] = useState(false);
  const [stats, setStats] = useState<GrabberStats>(emptyGrabberStats());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [durationTick, setDurationTick] = useState(0);
  const [chartData, setChartData] = useState<
    Array<{ time: string; processedIPs: number; foundDomains: number; errors: number; rate: number }>
  >([]);

  const apiUrl = `https://${vpsConfig.ip}:${vpsConfig.port}`;

  // ── Tour simulation override ───────────────────────────────────────────
  const simActive = !!tourSim;
  // When sim is active OR tutorial mode is on, bypass the grabber-access gate
  const hasGrabberAccess = (licenseData?.grabber === true) || simActive || !!tutorialMode;

  /** Format simulated elapsed seconds into h/m/s string */
  const fmtSimElapsed = (secs: number): string => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m ${s.toString().padStart(2, '0')}s`;
    if (m > 0) return `${m}m ${s.toString().padStart(2, '0')}s`;
    return `${s}s`;
  };

  useEffect(() => {
    if (hasGrabberAccess) {
      fetchGrabberStats();
      const interval = setInterval(fetchGrabberStats, 2000);
      return () => clearInterval(interval);
    }
  }, [hasGrabberAccess]);

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!isGrabbing) return;
    const interval = setInterval(() => setDurationTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [isGrabbing]);

  const fetchGrabberStats = async () => {
    try {
      const response = await fetch(`${apiUrl}/grabber-stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-License-Key': licenseData?.licenseKey || '',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setIsGrabbing(data.isGrabbing);
        setStats(data.stats);

        const now = new Date();
        const timeLabel = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;

        setChartData((prev) => [
          ...prev,
          {
            time: timeLabel,
            processedIPs: data.stats.processedIPs,
            foundDomains: data.stats.foundDomains,
            errors: data.stats.errors,
            rate: data.stats.rate,
          },
        ]);
      }
    } catch (err) {
      console.error('Erreur récupération stats grabber:', err);
    }
  };

  const startGrabber = async (mode: string) => {
    if (isScanning) {
      setError("Cannot start the grabber while a scan is running");
      return;
    }

    setLoading(true);
    setError(null);
    setShowModeSelector(false);

    const normalizedMode = mode.toUpperCase();

    try {
      const response = await fetch(`${apiUrl}/start-scan-grabber`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-License-Key': licenseData?.licenseKey || '',
        },
        body: JSON.stringify({ mode: normalizedMode }),
      });

      if (response.ok) {
        const data = await response.json();
        setIsGrabbing(true);
        setStats(data.stats);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Error starting grabber');
      }
    } catch {
      setError('Could not reach your VPS');
    } finally {
      setLoading(false);
    }
  };

  const handleStartClick = () => {
    setShowModeSelector(true);
  };

  const stopGrabber = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiUrl}/stop-scan-grabber`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-License-Key': licenseData?.licenseKey || '',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setIsGrabbing(false);
        setStats(data.stats);
        setShowSuccessBanner(true);
        setTimeout(() => {
          setShowSuccessBanner(false);
        }, 5000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Error stopping grabber');
      }
    } catch {
      setError('Could not reach your VPS');
    } finally {
      setLoading(false);
    }
  };

  if (!hasGrabberAccess && !previewMode) {
    return (
      <div className="cracker-shell cracker-shell--atlas">
        <div className="cracker-panel">
          <div className="gm-locked">
            <div className="gm-locked-icon">⊘</div>
            <p className="cracker-label mb-2">Harvest Engine</p>
            <h3 className="cracker-heading mb-2">Restricted access</h3>
            <p className="text-sm text-[#737373] max-w-sm mx-auto">
              This workspace requires a license with harvest engine access.
            </p>
            <div className="gm-locked-alert">
              Your current license does not include the harvest engine extension.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatNumber = formatGrabberCount;
  const formatRate = formatGrabberRate;
  void durationTick;

  // ── Simulation display values (override real state when simActive) ──────
  const displayIsGrabbing  = simActive ? true   : isGrabbing;
  const displayStats       = simActive
    ? {
        ...emptyGrabberStats(),
        foundDomains:  tourSim!.foundDomains,
        rate:          tourSim!.rate,
        processedIPs:  tourSim!.processedIPs,
        generatedIPs:  tourSim!.processedIPs * 3,
        totalDomains:  tourSim!.target,
        duplicateIPs:  0,
        errors:        0,
        startTime:     new Date(Date.now() - tourSim!.simElapsedSecs * 1_000).toISOString(),
      }
    : stats;
  const displayChartData   = simActive ? tourSim!.chartPoints : chartData;
  const displaySessionDuration = simActive
    ? fmtSimElapsed(tourSim!.simElapsedSecs)
    : formatSessionDuration(stats.startTime, isGrabbing);

  const successRate =
    displayStats.processedIPs > 0
      ? Math.min((displayStats.foundDomains / displayStats.processedIPs) * 100, 100)
      : displayStats.foundDomains > 0
        ? 100
        : 0;

  const efficiencyRate = displayStats.rate > 0 ? Math.min((displayStats.rate / 1000) * 100, 100) : 0;

  return (
    <div className="ix-page ix-page--module cracker-shell cracker-shell--atlas">
      <header className="ix-command-bar">
        <div className="cracker-panel-title min-w-0">
          <div className={`cracker-panel-icon${displayIsGrabbing ? ' cracker-panel-icon--active' : ''}`}>
            <GrabberIcon size={18} />
          </div>
          <div className="min-w-0">
            <p className="cracker-label">Harvest Engine</p>
            <h2 className="cracker-heading text-base sm:text-lg">List builder</h2>
          </div>
        </div>
        <div className="ix-command-bar__actions">
          {isScanning && !simActive && (
            <span className="gm-scan-lock text-xs">Lane busy</span>
          )}
          {simActive && (
            <span className="gm-scan-lock text-xs" style={{ background: 'rgba(229, 57, 53,.08)', borderColor: 'rgba(229, 57, 53,.2)', color: '#e53935' }}>
              Sim
            </span>
          )}
          <span className="gm-control-status text-xs">
            <span className={`gm-control-dot${displayIsGrabbing ? ' gm-control-dot--live' : ''}`} />
            {displayIsGrabbing ? 'Live' : 'Idle'}
          </span>
          {!displayIsGrabbing ? (
            <button
              type="button"
              onClick={handleStartClick}
              disabled={loading || isScanning}
              className="cracker-btn cracker-btn--gold"
              data-tour="grabber-start"
            >
              {loading ? 'Arming…' : isScanning ? 'Busy' : 'Arm'}
            </button>
          ) : (
            <button
              type="button"
              onClick={simActive ? simOnStop : stopGrabber}
              disabled={!simActive && loading}
              className="cracker-btn cracker-btn--stop"
              data-tour="grabber-stop"
            >
              {!simActive && loading ? '…' : 'Halt'}
            </button>
          )}
        </div>
      </header>

      <section className="ix-tile ix-harvest-metrics">
        <div className="ix-inline-metrics">
          <div className="cracker-metric">
            <div className="cracker-metric-value cracker-metric-value--gold">{formatNumber(displayStats.foundDomains)}</div>
            <div className="cracker-metric-label">Domains</div>
          </div>
          <div className="cracker-metric">
            <div className="cracker-metric-value">{formatRate(displayStats.rate)}</div>
            <div className="cracker-metric-label">Rate</div>
          </div>
          <div className="cracker-metric">
            <div className="cracker-metric-value">{displayIsGrabbing ? displaySessionDuration : '—'}</div>
            <div className="cracker-metric-label">Uptime</div>
          </div>
          <div className="cracker-metric">
            <div className={`cracker-metric-value ${displayIsGrabbing ? 'cracker-metric-value--green' : ''}`}>
              {displayIsGrabbing ? 'ON' : 'OFF'}
            </div>
            <div className="cracker-metric-label">Engine</div>
          </div>
        </div>
      </section>

      <AnimatePresence>
          {error && (
            <motion.div
              className="cracker-alert mt-3"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
            >
              <span aria-hidden="true">⚠</span>
              <span>{error}</span>
            </motion.div>
          )}
          {showSuccessBanner && (
            <motion.div
              className="gm-alert-success mt-3"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
            >
              <span aria-hidden="true">✓</span>
              <div>
                <p className="gm-alert-success-title">Harvest halted</p>
                <p className="gm-alert-success-text">
                  List export was written to disk automatically.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="ix-bento ix-bento--harvest">
            {displayChartData.length > 0 && (
              <div className="ix-tile ix-harvest-chart">
                <div className="cracker-section-header">
                  <span className="cracker-section-title">Harvest waveform</span>
                  {displayIsGrabbing && <span className="cracker-badge cracker-badge--green">Live</span>}
                </div>
                <div className="gm-chart-wrap">
                  <LiveStatsChart
                    data={displayChartData}
                    dataKeys={[
                      { key: 'processedIPs', name: 'IPs processed', color: '#737373' },
                      { key: 'foundDomains', name: 'Domains found', color: '#e53935' },
                      { key: 'errors', name: 'Errors', color: '#fca5a5' },
                      { key: 'rate', name: 'Speed (IPs/s)', color: '#c62828' },
                    ]}
                    title=""
                    height={280}
                  />
                </div>
              </div>
            )}

            <div className="ix-tile ix-harvest-perf">
              <div className="cracker-section-header">
                <span className="cracker-section-title">Quality bars</span>
              </div>
              <div className="mb-4">
                <div className="flex justify-between mb-1">
                  <span className="mm-detail-label">Success rate</span>
                  <span className="mm-detail-value mm-detail-value--green">{successRate.toFixed(1)}%</span>
                </div>
                <div className="gm-progress-track">
                  <div
                    className="gm-progress-fill gm-progress-fill--green"
                    style={{ width: `${successRate}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="mm-detail-label">Efficiency</span>
                  <span className="mm-detail-value mm-detail-value--gold">{efficiencyRate.toFixed(1)}%</span>
                </div>
                <div className="gm-progress-track">
                  <div
                    className="gm-progress-fill gm-progress-fill--gold"
                    style={{ width: `${efficiencyRate}%` }}
                  />
                </div>
              </div>
            </div>

          <div className="ix-harvest-aside">
            <div className="ix-tile">
              <div className="cracker-section-header">
                <span className="cracker-section-title">Depth metrics</span>
              </div>
              <div className="mm-stats-grid mb-4">
                <div className="mm-stats-cell">
                  <div className={`mm-stats-value ${displayIsGrabbing ? 'mm-detail-value--green' : ''}`}>
                    {displayIsGrabbing ? 'LIVE' : 'IDLE'}
                  </div>
                  <div className="mm-stats-label">Status</div>
                </div>
                <div className="mm-stats-cell">
                  <div className="mm-stats-value mm-detail-value--gold">
                    {formatNumber(displayStats.totalDomains)}
                  </div>
                  <div className="mm-stats-label">Total</div>
                </div>
                <div className="mm-stats-cell">
                  <div className="mm-stats-value">{formatRate(displayStats.rate)}</div>
                  <div className="mm-stats-label">Speed</div>
                </div>
                <div className="mm-stats-cell">
                  <div className="mm-stats-value">
                    {displayIsGrabbing ? displaySessionDuration : '—'}
                  </div>
                  <div className="mm-stats-label">Duration</div>
                </div>
              </div>

              <div className="cracker-section-header mb-2">
                <span className="cracker-section-title">Session details</span>
              </div>
              <div className="mm-detail-row">
                <span className="mm-detail-label">Requests processed</span>
                <span className="mm-detail-value">{formatNumber(displayStats.processedIPs)}</span>
              </div>
              <div className="mm-detail-row">
                <span className="mm-detail-label">Requests generated</span>
                <span className="mm-detail-value">{formatNumber(displayStats.generatedIPs)}</span>
              </div>
              <div className="mm-detail-row">
                <span className="mm-detail-label">Duplicates</span>
                <span className="mm-detail-value">{formatNumber(displayStats.duplicateIPs)}</span>
              </div>
              <div className="mm-detail-row">
                <span className="mm-detail-label">Errors</span>
                <span className="mm-detail-value mm-detail-value--red">{formatNumber(displayStats.errors)}</span>
              </div>
              <div className="mm-detail-row">
                <span className="mm-detail-label">Uptime</span>
                <span className="mm-detail-value mm-detail-value--gold">{displaySessionDuration}</span>
              </div>
            </div>

            <div className="ix-tile">
              <div className="cracker-section-header">
                <span className="cracker-section-title">Run notes</span>
              </div>
              <div className="gm-spec-list">
                {[
                  'TURBO — fast mode with solid result quality',
                  'VULN — targets vulnerable URLs only (slow, high quality)',
                  'The .txt export saves automatically when you stop',
                  'Cannot start while a scan is active',
                ].map((item) => (
                  <div key={item} className="gm-spec-item">
                    <span className="gm-spec-dot" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

      {/* Mode Selector Modal */}
      {showModeSelector && (
        <div className="cracker-modal-overlay" onClick={() => setShowModeSelector(false)}>
          <div className="cracker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cracker-modal-header">
              <div>
                <p className="cracker-label mb-1">Harvest profile</p>
                <h3 className="cracker-heading">Pick a lane</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowModeSelector(false)}
                className="gm-modal-close"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="cracker-modal-body">
              <p className="text-xs text-[#737373] mb-4 leading-relaxed">
                Choose the mode that fits your speed and quality needs.
              </p>

              <div className="gm-mode-grid">
                <div
                  className="gm-mode-card"
                  onClick={() => startGrabber('GLITCH')}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') startGrabber('GLITCH');
                  }}
                >
                  <div className="gm-mode-icon">G</div>
                  <p className="gm-mode-name">TURBO</p>
                  <p className="gm-mode-tagline">Fast &amp; balanced</p>
                  <div className="gm-mode-specs">
                    <div className="gm-mode-spec">
                      <span>Speed</span>
                      <span className="gm-mode-spec-value gm-mode-spec-value--gold">FAST</span>
                    </div>
                    <div className="gm-mode-spec">
                      <span>Quality</span>
                      <span className="gm-mode-spec-value gm-mode-spec-value--green">SOLID</span>
                    </div>
                  </div>
                </div>

                <div
                  className="gm-mode-card"
                  onClick={() => startGrabber('VULN')}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') startGrabber('VULN');
                  }}
                >
                  <div className="gm-mode-icon">V</div>
                  <p className="gm-mode-name">VULN</p>
                  <p className="gm-mode-tagline">Vulnerable URLs only</p>
                  <div className="gm-mode-specs">
                    <div className="gm-mode-spec">
                      <span>Speed</span>
                      <span className="gm-mode-spec-value gm-mode-spec-value--muted">SLOW</span>
                    </div>
                    <div className="gm-mode-spec">
                      <span>Quality</span>
                      <span className="gm-mode-spec-value gm-mode-spec-value--green">PEAK</span>
                    </div>
                    <div className="gm-mode-spec">
                      <span>Profile</span>
                      <span className="gm-mode-spec-value gm-mode-spec-value--gold">SPECIAL</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="cracker-modal-footer">
              <button
                type="button"
                onClick={() => setShowModeSelector(false)}
                className="cracker-btn cracker-btn--ghost"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GrabberManager;
